import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  Icons,
  iconSize,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TagChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FILE_DOCUMENT_KINDS,
  type FileDocument,
  type FileDocumentKind,
} from '@munifocus/contracts-gov';
import { useDeleteDocument, useDocuments, useUploadDocument } from '../lib/documents-api';
import { formatDateTime } from '../lib/relative-time';
import { useConfirmAction } from '../lib/useConfirmAction';
import { useSubmitValidation } from '../lib/useSubmitValidation';

/** Mirrors ACCEPTED_CONTENT_TYPES in gov-api's DocumentsService. */
const ACCEPT = '.pdf,.doc,.docx,.jpg,.jpeg,.png,.heic,.tif,.tiff';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function DocumentRow({
  doc,
  locale,
  onDelete,
  deleting,
}: {
  doc: FileDocument;
  locale: string;
  /** Absent for a read-only seat: the row is listed without its bin. */
  onDelete?: (id: string) => void;
  deleting: boolean;
}) {
  const { t } = useTranslation();
  const meta = [
    doc.authorName,
    // Date-only value stored at UTC midnight: format in UTC so it doesn't slip
    // to the previous day for viewers in negative offsets (same fix as #456).
    doc.documentDate
      ? formatDateTime(doc.documentDate, locale, { dateStyle: 'medium', timeZone: 'UTC' })
      : null,
    formatSize(doc.sizeBytes),
  ].filter(Boolean);

  return (
    <Stack direction="row" sx={{ py: 1, gap: 2, alignItems: 'flex-start' }}>
      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
        <Stack
          useFlexGap
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {doc.title}
          </Typography>
          <TagChip label={t(`gov.documents.kind.${doc.kind}`)} />
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {meta.join(' · ')}
        </Typography>
        {doc.note ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {doc.note}
          </Typography>
        ) : null}
        <Link
          href={doc.downloadUrl}
          target="_blank"
          rel="noopener"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}
        >
          <Icons.Download size={iconSize.sm} aria-hidden="true" />
          <Typography variant="caption">{doc.originalFilename}</Typography>
        </Link>
      </Box>
      {onDelete && (
        <IconButton
          size="small"
          disabled={deleting}
          onClick={() => onDelete(doc.id)}
          aria-label={t('gov.documents.remove', { title: doc.title })}
        >
          <Icons.Trash2 size={iconSize.md} />
        </IconButton>
      )}
    </Stack>
  );
}

/**
 * The supporting-document surface for a file: expert reports (s. 131(3)) and
 * third-party documents the officer receives from outside the system.
 *
 * Pass `kind` to pin the component to a single kind — Stage 4 uses that to show
 * only the expert report and skip the kind selector. Without it, every
 * attachment on the file is listed and the officer chooses the kind.
 */
export function FileAttachments({
  fileId,
  kind: pinnedKind,
  onCountChange,
  canAttach = true,
}: {
  fileId: string;
  kind?: FileDocumentKind;
  /** Notified with the current attachment count — lets Stage 4 gate on it. */
  onCountChange?: (count: number) => void;
  /** False for a read-only seat: attachments are listed and downloadable only. */
  canAttach?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const documents = useDocuments(fileId);
  const upload = useUploadDocument(fileId);
  const remove = useDeleteDocument(fileId);
  const confirm = useConfirmAction();
  const inputRef = useRef<HTMLInputElement>(null);

  const [picked, setPicked] = useState<File | null>(null);
  const [kind, setKind] = useState<FileDocumentKind>(pinnedKind ?? 'third_party');
  const [title, setTitle] = useState('');
  const [authorName, setAuthorName] = useState('');
  const [documentDate, setDocumentDate] = useState('');
  const [note, setNote] = useState('');

  const all = documents.data ?? [];
  const listed = pinnedKind ? all.filter((d) => d.kind === pinnedKind) : all;

  // Report the count up so the parent's gate tracks uploads and deletions
  // without duplicating the query. Only once the query has actually resolved —
  // an empty list while loading would read as "nothing attached".
  const loaded = documents.isSuccess;
  const count = listed.length;
  useEffect(() => {
    if (loaded) onCountChange?.(count);
  }, [loaded, count, onCountChange]);

  const reset = () => {
    v.reset();
    setPicked(null);
    setTitle('');
    setAuthorName('');
    setDocumentDate('');
    setNote('');
    if (!pinnedKind) setKind('third_party');
  };

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    const chosen = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!chosen) return;
    setPicked(chosen);
    // Seed the title from the filename minus its extension — the officer edits it.
    if (!title) setTitle(chosen.name.replace(/\.[^.]+$/, ''));
  };

  const v = useSubmitValidation({
    title: title.trim() ? null : t('common.validation.required'),
  });

  const attach = () => {
    if (!picked || title.trim() === '') return;
    upload.mutate(
      { document: picked, fields: { kind, title, authorName, documentDate, note } },
      { onSuccess: reset },
    );
  };

  return (
    <Box>
      <Typography variant="overline">
        {pinnedKind ? t(`gov.documents.kind.${pinnedKind}`) : t('gov.documents.title')}
      </Typography>

      {documents.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {documents.isError && <Alert severity="error">{t('gov.documents.loadError')}</Alert>}

      {documents.isSuccess && listed.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('gov.documents.empty')}
        </Typography>
      )}

      <Stack divider={<Divider flexItem />} sx={{ mt: 0.5 }}>
        {listed.map((doc) => (
          <DocumentRow
            key={doc.id}
            doc={doc}
            locale={i18n.language}
            onDelete={
              canAttach
                ? async (id) => {
                    // One tap on a bin icon sitting under an evidence download is too
                    // easy to land by accident on a phone.
                    if (
                      !(await confirm({
                        title: t('gov.documents.removeTitle'),
                        body: t('gov.documents.removeBody', { title: doc.title }),
                        confirmLabel: t('gov.documents.removeConfirm'),
                        tone: 'danger',
                      }))
                    ) {
                      return;
                    }
                    remove.mutate(id);
                  }
                : undefined
            }
            deleting={remove.isPending}
          />
        ))}
      </Stack>

      {upload.isError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {t('gov.documents.uploadError')}
        </Alert>
      )}

      <Box sx={{ mt: 2 }}>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onPick}
          style={{ display: 'none' }}
          data-testid="document-input"
        />
        {!canAttach ? null : !picked ? (
          <Button
            variant="outlined"
            startIcon={<Icons.Paperclip size={iconSize.md} />}
            onClick={() => inputRef.current?.click()}
          >
            {t('gov.documents.attach')}
          </Button>
        ) : (
          <Stack spacing={1.5}>
            <Typography variant="body2" color="text.secondary">
              {picked.name} · {formatSize(picked.size)}
            </Typography>

            {!pinnedKind && (
              <FormControl fullWidth size="small">
                <InputLabel>{t('gov.documents.kindLabel')}</InputLabel>
                <Select
                  label={t('gov.documents.kindLabel')}
                  aria-label={t('gov.documents.kindLabel')}
                  value={kind}
                  onChange={(e) => setKind(e.target.value as FileDocumentKind)}
                >
                  {FILE_DOCUMENT_KINDS.map((k) => (
                    <MenuItem key={k} value={k}>
                      {t(`gov.documents.kind.${k}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              size="small"
              required
              label={t('gov.documents.docTitle')}
              {...v.field('title')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              label={t('gov.documents.author')}
              value={authorName}
              onChange={(e) => setAuthorName(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('gov.documents.documentDate')}
              slotProps={{ inputLabel: { shrink: true } }}
              value={documentDate}
              onChange={(e) => setDocumentDate(e.target.value)}
            />
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label={t('gov.documents.note')}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<Icons.Upload size={iconSize.md} />}
                disabled={upload.isPending}
                onClick={v.guard(attach)}
              >
                {t('gov.documents.upload')}
              </Button>
              <Button variant="text" disabled={upload.isPending} onClick={reset}>
                {t('gov.documents.discard')}
              </Button>
            </Stack>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
